import { useEffect, useState } from "react";
import {Leaf, Car, Smartphone, Plane, Home, Zap, TreeDeciduous, Info, Share2, TrendingUp, ChevronDown, ChevronUp} from 'lucide-react';

interface EquivalenciesVisualizerProps {
  emissions: number;
  className?: string;
}

export default function EquivalenciesVisualizer({ emissions, className = "" }: EquivalenciesVisualizerProps) {
  const [animatedValues, setAnimatedValues] = useState({
    trees: 0,
    miles: 0,
    phones: 0,
    flights: 0,
    homes: 0,
    bulbs: 0,
  });
  const [showComparison, setShowComparison] = useState(false);
  const [unitSystem, setUnitSystem] = useState<'us' | 'metric'>('us');
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  // Calculate equivalencies
  const equivalencies = {
    trees: Math.round(emissions * 16), // trees needed to offset
    miles: Math.round(emissions * 2485), // miles driven
    phones: Math.round(emissions * 121643), // smartphone charges
    flights: Math.round(emissions * 0.556), // one-way flights NYC-LA
    homes: Math.round(emissions * 0.091), // homes' electricity for a year
    bulbs: Math.round(emissions * 7305), // LED bulb hours
  };

  // Average US person emissions: 16 tons CO2e per year
  const avgEmissions = 16;
  const comparisonPercent = Math.round((emissions / avgEmissions) * 100);

  // Unit conversions
  const convertedValues = {
    distance: unitSystem === 'us' ? animatedValues.miles : Math.round(animatedValues.miles * 1.609), // km
    distanceLabel: unitSystem === 'us' ? 'miles' : 'kilometers',
  };

  const shareEquivalency = (name: string, value: string, description: string) => {
    const text = `My carbon footprint equals ${value} ${description}! Calculate yours at www.greenhousegascalculator.com`;
    if (navigator.share) {
      navigator.share({ title: 'My Carbon Footprint', text, url: window.location.href });
    } else {
      navigator.clipboard.writeText(text);
      alert('Copied to clipboard!');
    }
  };

  // Animate numbers on mount
  useEffect(() => {
    const duration = 2000; // 2 seconds
    const steps = 60;
    const interval = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setAnimatedValues({
        trees: Math.round(equivalencies.trees * progress),
        miles: Math.round(equivalencies.miles * progress),
        phones: Math.round(equivalencies.phones * progress),
        flights: Math.round(equivalencies.flights * progress),
        homes: parseFloat((equivalencies.homes * progress).toFixed(2)),
        bulbs: Math.round(equivalencies.bulbs * progress),
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setAnimatedValues(equivalencies);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [emissions]);

  return (
    <div className={className}>
      {/* Interactive Controls */}
      <div className="flex flex-wrap gap-4 mb-8 justify-center items-center">
        <button
          onClick={() => setShowComparison(!showComparison)}
          className={`px-6 py-2 rounded-full font-medium transition-all ${
            showComparison
              ? 'bg-[#2E8B57] text-white shadow-md'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <TrendingUp className="w-4 h-4 inline mr-2" />
          Compare to Average
        </button>
        <div className="flex items-center gap-2 bg-gray-100 rounded-full p-1">
          <button
            onClick={() => setUnitSystem('us')}
            className={`px-4 py-1 rounded-full font-medium transition-all ${
              unitSystem === 'us' ? 'bg-white shadow-sm' : 'text-gray-600'
            }`}
          >
            US Units
          </button>
          <button
            onClick={() => setUnitSystem('metric')}
            className={`px-4 py-1 rounded-full font-medium transition-all ${
              unitSystem === 'metric' ? 'bg-white shadow-sm' : 'text-gray-600'
            }`}
          >
            Metric
          </button>
        </div>
      </div>

      {/* Comparison Bar */}
      {showComparison && (
        <div className="mb-8 bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-2xl border border-green-200 animate-fade-in">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Your Emissions vs Average US Person</h3>
            <span className={`text-2xl font-bold ${
              emissions < avgEmissions ? 'text-green-600' : emissions > avgEmissions ? 'text-orange-600' : 'text-gray-700'
            }`}>
              {comparisonPercent}%
            </span>
          </div>
          <div className="relative h-8 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`absolute h-full rounded-full transition-all duration-1000 ${
                emissions < avgEmissions ? 'bg-green-500' : emissions > avgEmissions ? 'bg-orange-500' : 'bg-gray-500'
              }`}
              style={{ width: `${Math.min(comparisonPercent, 100)}%` }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-sm font-medium text-gray-700">
                You: {emissions}t | Average: {avgEmissions}t
              </span>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-3 text-center">
            {emissions < avgEmissions
              ? `Great! You're ${Math.round(((avgEmissions - emissions) / avgEmissions) * 100)}% below average. Keep it up!`
              : emissions > avgEmissions
              ? `You're ${Math.round(((emissions - avgEmissions) / avgEmissions) * 100)}% above average. Let's find reduction opportunities!`
              : "You're right at the average. Small changes can make a big difference!"}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {/* Trees */}
      <div
        className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl shadow-sm border border-green-200 hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer"
        onClick={() => setExpandedCard(expandedCard === 'trees' ? null : 'trees')}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-green-600 p-3 rounded-xl">
              <TreeDeciduous className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Trees to Offset</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                shareEquivalency('trees', animatedValues.trees.toLocaleString(), 'trees planted');
              }}
              className="p-2 hover:bg-green-200 rounded-lg transition-colors"
              title="Share this equivalency"
            >
              <Share2 className="w-4 h-4 text-green-700" />
            </button>
            {expandedCard === 'trees' ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
          </div>
        </div>
        <div className="relative">
          <p className="text-4xl font-bold text-green-600 mb-2">
            {animatedValues.trees.toLocaleString()}
          </p>
          <div className="h-2 bg-green-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-600 rounded-full transition-all duration-2000"
              style={{ width: "100%" }}
            />
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-3">
          trees needed to absorb this CO₂ annually
        </p>
        {expandedCard === 'trees' && (
          <div className="mt-4 pt-4 border-t border-green-200 space-y-2 animate-fade-in">
            <p className="text-xs text-gray-700">
              <Info className="w-3 h-3 inline mr-1" />
              <strong>Methodology:</strong> Based on average mature tree absorption of ~48 lbs CO₂/year.
            </p>
            <p className="text-xs text-gray-700">
              💡 <strong>Reduction Tip:</strong> Plant native species or support reforestation projects.
            </p>
          </div>
        )}
      </div>

      {/* Miles/Kilometers Driven */}
      <div
        className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl shadow-sm border border-blue-200 hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer"
        onClick={() => setExpandedCard(expandedCard === 'miles' ? null : 'miles')}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 p-3 rounded-xl">
              <Car className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">{unitSystem === 'us' ? 'Miles' : 'Kilometers'} Driven</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                shareEquivalency('car', convertedValues.distance.toLocaleString(), convertedValues.distanceLabel);
              }}
              className="p-2 hover:bg-blue-200 rounded-lg transition-colors"
              title="Share this equivalency"
            >
              <Share2 className="w-4 h-4 text-blue-700" />
            </button>
            {expandedCard === 'miles' ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
          </div>
        </div>
        <div className="relative">
          <p className="text-4xl font-bold text-blue-600 mb-2">
            {convertedValues.distance.toLocaleString()}
          </p>
          <div className="h-2 bg-blue-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 rounded-full transition-all duration-2000"
              style={{ width: "100%" }}
            />
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-3">
          {convertedValues.distanceLabel} by an average gasoline car
        </p>
        {expandedCard === 'miles' && (
          <div className="mt-4 pt-4 border-t border-blue-200 space-y-2 animate-fade-in">
            <p className="text-xs text-gray-700">
              <Info className="w-3 h-3 inline mr-1" />
              <strong>Methodology:</strong> Based on 0.404 kg CO₂ per mile for average gasoline vehicle.
            </p>
            <p className="text-xs text-gray-700">
              💡 <strong>Reduction Tip:</strong> Switch to public transit, carpool, or electric vehicles.
            </p>
          </div>
        )}
      </div>

      {/* Smartphone Charges */}
      <div
        className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl shadow-sm border border-purple-200 hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer"
        onClick={() => setExpandedCard(expandedCard === 'phones' ? null : 'phones')}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-purple-600 p-3 rounded-xl">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Phone Charges</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                shareEquivalency('phones', animatedValues.phones.toLocaleString(), 'smartphone charges');
              }}
              className="p-2 hover:bg-purple-200 rounded-lg transition-colors"
              title="Share this equivalency"
            >
              <Share2 className="w-4 h-4 text-purple-700" />
            </button>
            {expandedCard === 'phones' ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
          </div>
        </div>
        <div className="relative">
          <p className="text-4xl font-bold text-purple-600 mb-2">
            {animatedValues.phones.toLocaleString()}
          </p>
          <div className="h-2 bg-purple-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-purple-600 rounded-full transition-all duration-2000"
              style={{ width: "100%" }}
            />
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-3">
          smartphone charges (0-100%)
        </p>
        {expandedCard === 'phones' && (
          <div className="mt-4 pt-4 border-t border-purple-200 space-y-2 animate-fade-in">
            <p className="text-xs text-gray-700">
              <Info className="w-3 h-3 inline mr-1" />
              <strong>Methodology:</strong> Based on ~8.22 grams CO₂ per full smartphone charge.
            </p>
            <p className="text-xs text-gray-700">
              💡 <strong>Reduction Tip:</strong> Unplug chargers when not in use; use renewable energy sources.
            </p>
          </div>
        )}
      </div>

      {/* Flights */}
      <div
        className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-2xl shadow-sm border border-orange-200 hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer"
        onClick={() => setExpandedCard(expandedCard === 'flights' ? null : 'flights')}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-orange-600 p-3 rounded-xl">
              <Plane className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Flights</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                shareEquivalency('flights', animatedValues.flights.toLocaleString(), 'flights NYC-LA');
              }}
              className="p-2 hover:bg-orange-200 rounded-lg transition-colors"
              title="Share this equivalency"
            >
              <Share2 className="w-4 h-4 text-orange-700" />
            </button>
            {expandedCard === 'flights' ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
          </div>
        </div>
        <div className="relative">
          <p className="text-4xl font-bold text-orange-600 mb-2">
            {animatedValues.flights.toLocaleString()}
          </p>
          <div className="h-2 bg-orange-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-orange-600 rounded-full transition-all duration-2000"
              style={{ width: "100%" }}
            />
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-3">
          one-way flights NYC to LA
        </p>
        {expandedCard === 'flights' && (
          <div className="mt-4 pt-4 border-t border-orange-200 space-y-2 animate-fade-in">
            <p className="text-xs text-gray-700">
              <Info className="w-3 h-3 inline mr-1" />
              <strong>Methodology:</strong> Based on ~1.8 tons CO₂ per cross-country flight.
            </p>
            <p className="text-xs text-gray-700">
              💡 <strong>Reduction Tip:</strong> Fly economy, choose direct flights, or opt for train travel when possible.
            </p>
          </div>
        )}
      </div>

      {/* Home Energy */}
      <div
        className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-2xl shadow-sm border border-yellow-200 hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer"
        onClick={() => setExpandedCard(expandedCard === 'homes' ? null : 'homes')}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-yellow-600 p-3 rounded-xl">
              <Home className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Home Energy</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                shareEquivalency('homes', animatedValues.homes.toLocaleString(), 'homes powered for 1 year');
              }}
              className="p-2 hover:bg-yellow-200 rounded-lg transition-colors"
              title="Share this equivalency"
            >
              <Share2 className="w-4 h-4 text-yellow-700" />
            </button>
            {expandedCard === 'homes' ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
          </div>
        </div>
        <div className="relative">
          <p className="text-4xl font-bold text-yellow-600 mb-2">
            {animatedValues.homes.toLocaleString()}
          </p>
          <div className="h-2 bg-yellow-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-yellow-600 rounded-full transition-all duration-2000"
              style={{ width: "100%" }}
            />
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-3">
          homes' electricity for 1 year
        </p>
        {expandedCard === 'homes' && (
          <div className="mt-4 pt-4 border-t border-yellow-200 space-y-2 animate-fade-in">
            <p className="text-xs text-gray-700">
              <Info className="w-3 h-3 inline mr-1" />
              <strong>Methodology:</strong> Based on 10,972 kWh average annual home electricity use.
            </p>
            <p className="text-xs text-gray-700">
              💡 <strong>Reduction Tip:</strong> Switch to LED bulbs, install solar panels, improve insulation.
            </p>
          </div>
        )}
      </div>

      {/* LED Bulbs */}
      <div
        className="bg-gradient-to-br from-cyan-50 to-cyan-100 p-6 rounded-2xl shadow-sm border border-cyan-200 hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer"
        onClick={() => setExpandedCard(expandedCard === 'bulbs' ? null : 'bulbs')}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-cyan-600 p-3 rounded-xl">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">LED Hours</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                shareEquivalency('bulbs', animatedValues.bulbs.toLocaleString(), 'LED hours');
              }}
              className="p-2 hover:bg-cyan-200 rounded-lg transition-colors"
              title="Share this equivalency"
            >
              <Share2 className="w-4 h-4 text-cyan-700" />
            </button>
            {expandedCard === 'bulbs' ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
          </div>
        </div>
        <div className="relative">
          <p className="text-4xl font-bold text-cyan-600 mb-2">
            {animatedValues.bulbs.toLocaleString()}
          </p>
          <div className="h-2 bg-cyan-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-cyan-600 rounded-full transition-all duration-2000"
              style={{ width: "100%" }}
            />
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-3">
          hours of LED bulb usage (10W)
        </p>
        {expandedCard === 'bulbs' && (
          <div className="mt-4 pt-4 border-t border-cyan-200 space-y-2 animate-fade-in">
            <p className="text-xs text-gray-700">
              <Info className="w-3 h-3 inline mr-1" />
              <strong>Methodology:</strong> Based on 10W LED bulb with grid average 0.92 kg CO₂/kWh.
            </p>
            <p className="text-xs text-gray-700">
              💡 <strong>Reduction Tip:</strong> Switch all bulbs to LED; use natural lighting when possible.
            </p>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
